import React, { useMemo } from 'react';
import { useMediaQuery } from '@material-ui/core';
import { useAITTheme, type AITTheme } from '@this/shared/ui/theme';
import { Loading } from '@this/src/components/shared/ui/feedbacks/loading';
import { digits } from '@this/src/util/price/price.util';
import SelectTransportBoxPriceRangeLabel from '../price_range_label';

type Props = {
  loading: boolean;
  selected: boolean;
  hovered: boolean;
  price?: number;
  isAdditional?: boolean;
  priceRange?: string;
  showsPricePerPersonText?: boolean;
  changeable?: boolean;
  onClick: () => void;
  onTouchStart: () => void;
  onMouseOver: () => void;
  onMouseLeave: () => void;
  seatType?: string | null;
  isPackage?: true | null;
  seatCount?: string | null;
  seatCountLoading?: boolean;
  isDomestic?: boolean;
  disabled?: boolean;
  intlSpCandidatePrice?: boolean;
};

const SelectTransportBoxRightInListTemplate: React.FC<Props> = ({
  loading,
  selected,
  hovered,
  price,
  isAdditional,
  priceRange,
  showsPricePerPersonText,
  changeable,
  onClick,
  onTouchStart,
  onMouseOver,
  onMouseLeave,
  isPackage,
  seatCount,
  seatCountLoading,
  isDomestic,
  disabled,
  intlSpCandidatePrice
}) => {
  const { theme } = useAITTheme();
  const isSp = useMediaQuery('(max-width:768px)');
  const styles = useMemo(() => getStyles(theme), [theme]);
  const hasSeatCount = seatCount != null || seatCountLoading;
  const isSelected = selected || hovered;

  const transportButtonStyle = useMemo(
    () => styles.transportButton({ selected: isSelected, disabled, hasSeatCount, isPackage, isDomestic }),
    [styles, isSelected, disabled, hasSeatCount, isPackage, isDomestic]
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', width: '100%', padding: '0 10px 10px' }}>
      <div
        style={transportButtonStyle}
        onClick={onClick}
        onTouchStart={onTouchStart}
        onMouseOver={onMouseOver}
        onMouseLeave={onMouseLeave}
      >
        {loading ? (
          <div style={styles.rightBody}>
            <Loading />
          </div>
        ) : hovered && !selected ? (
          <div className="select-transport-box__right__body__select">選択する</div>
        ) : (
          <div style={styles.rightBox} className="select-transport-box__right__body">
            {changeable != null && (
              <div style={styles.changeable(selected)}>
                <div style={styles.rightChangeable} className="select-transport-box__right__body__changeable flex">
                  変更
                  <br />
                  {changeable ? '可' : '不可'}
                </div>
              </div>
            )}
            <div style={styles.details(selected)}>
              <div>
                <span style={styles.rightPrice(intlSpCandidatePrice === true && isSp)}>
                  {price !== undefined && (
                    <>
                      {isAdditional && <>+ </>}
                      <span data-wovn-ignore>{digits(price)}</span>
                      <span style={styles.yen}>円</span>
                    </>
                  )}
                  {priceRange && <SelectTransportBoxPriceRangeLabel priceRange={priceRange} />}
                </span>
                <span style={styles.rightPeopleNum}>{showsPricePerPersonText ? '/1名' : ''}</span>
              </div>
            </div>
          </div>
        )}
      </div>
      {seatCountLoading ? (
        <span style={styles.seatCountMark({ disabled: false })}>
          <Loading size="small" />
        </span>
      ) : seatCount != null ? (
        <span style={styles.seatCountMark({ disabled })}>{seatCount}</span>
      ) : null}
    </div>
  );
};

SelectTransportBoxRightInListTemplate.defaultProps = {
  showsPricePerPersonText: true
};

type TransportButtonParams = {
  selected?: boolean;
  disabled?: boolean;
  hasSeatCount?: boolean;
  isPackage?: true | null;
  isDomestic?: boolean;
};

type SeatCountMarkParams = {
  disabled?: boolean;
};

const getStyles = (theme: AITTheme) => ({
  transportButton: ({
    selected,
    disabled,
    hasSeatCount,
    isPackage,
    isDomestic
  }: TransportButtonParams): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0,
    border: `2px solid ${disabled ? theme.disabledColor : theme.linkColor}`,
    borderRight: hasSeatCount ? 'none' : `2px solid ${disabled ? theme.disabledColor : theme.linkColor}`,
    borderRadius: hasSeatCount ? '3px 0 0 3px' : '3px',
    padding: 0,
    margin: hasSeatCount ? '2px 0 2px 2px' : '2px',
    color: disabled ? theme.grayTextColor : theme.linkColor,
    backgroundColor: disabled ? theme.disabledColor : 'transparent',
    fontWeight: 'bold',
    cursor: disabled ? 'default' : 'pointer',
    minWidth: isPackage ? '145px' : 0,
    height: '3rem',
    flex: 1,
    ...(disabled ? { pointerEvents: 'none' as const } : {}),
    ...(!disabled && selected ? { background: theme.linkColor, color: 'white' } : {}),
    ...(isPackage ? { minHeight: '45px', textAlign: 'center' as const } : {})
  }),
  seatCountMark: ({ disabled }: SeatCountMarkParams): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: 'bold',
    width: '40px',
    flexShrink: 0,
    alignSelf: 'stretch',
    margin: '2px 2px 2px 0',
    border: `2px solid ${disabled ? theme.disabledColor : theme.linkColor}`,
    borderLeft: 'none',
    borderTopRightRadius: '3px',
    borderBottomRightRadius: '3px',
    backgroundColor: disabled ? theme.grayBgColor : '#f5f5f5',
    color: disabled ? theme.grayTextColor : theme.linkColor
  }),
  rightBox: {
    display: 'flex',
    width: '100%',
    height: '100%'
  } as React.CSSProperties,
  changeable: (selected?: boolean): React.CSSProperties => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '3rem',
    height: '100%',
    background: selected ? theme.accentColor : 'inherit'
  }),
  details: (selected?: boolean): React.CSSProperties => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    background: selected ? theme.accentColor : 'inherit'
  }),
  rightBody: {
    textAlign: 'center' as const,
    marginLeft: 'auto',
    marginRight: 'auto'
  } as React.CSSProperties,
  rightChangeable: {
    fontSize: '11px'
  } as React.CSSProperties,
  rightPrice: (isIntlSpCandidateOnSp: boolean): React.CSSProperties => ({
    fontSize: isIntlSpCandidateOnSp ? '18px' : '14px',
    ...(isIntlSpCandidateOnSp ? { fontWeight: 'bold', lineHeight: 1 } : {})
  }),
  rightPeopleNum: {
    fontSize: '11px'
  } as React.CSSProperties,
  yen: {
    fontSize: '11px'
  } as React.CSSProperties
});

export default SelectTransportBoxRightInListTemplate;
